<script lang="ts">
  /**
   * TimelineViewOptionsSheet - the mobile Timeline's scale / range / display
   * controls and favorites, in a bottom sheet (replaces the old header's chip
   * rows and star menu). Only offers what the current view supports, and builds
   * URLs with lib/timelineUrl, like the desktop Header.
   */
  import { router } from '@inertiajs/svelte';
  import MobileSheet from '../MobileSheet.svelte';
  import SegmentedControl from './SegmentedControl.svelte';
  import type { FavoriteView } from '../../lib/types';
  import {
    timelineUrlForDisplay,
    timelineUrlForRange,
    timelineUrlForScale,
    type TimelineState,
  } from '../../lib/timelineUrl';
  import {
    DISPLAY_LABELS,
    RANGE_LABELS,
    SCALE_LABELS,
    defaultFavoriteName,
    getDisplayOptions,
    getMobileFavorites,
    getRangeOptions,
    getScaleOptions,
    isFavoriteCurrent,
    isMobileTimelineView,
  } from '../../lib/mobileTimeline';
  import { favoriteViews, addFavorite } from '../../lib/stores/favorites';
  import { navigateToFavorite } from '../../lib/favoritesNavigation';
  import { isCoarsePointer } from '../../lib/stores/viewport';

  interface Props {
    open: boolean;
    onClose: () => void;
    timeline: TimelineState;
  }

  let { open, onClose, timeline }: Props = $props();

  // Desktop-only views have nothing to adjust on a phone: only favorites are offered.
  let supported = $derived(isMobileTimelineView(timeline.view));
  let scaleOptions = $derived(supported ? getScaleOptions(timeline.view, timeline.viewConfig) : []);
  let rangeOptions = $derived(
    supported ? getRangeOptions(timeline.view, timeline.scale, timeline.viewConfig) : [],
  );
  let displayOptions = $derived(
    supported ? getDisplayOptions(timeline.view, timeline.viewConfig) : [],
  );

  let favorites = $derived(getMobileFavorites($favoriteViews));
  let currentIsSaved = $derived($favoriteViews.some((f) => isFavoriteCurrent(f, timeline)));
  let canSave = $derived(supported && !currentIsSaved);

  // Every way out (Done, backdrop, Esc, swipe, an option) goes through here, so a
  // half-filled save form or an old error isn't there when the sheet reopens.
  function close() {
    saveFormOpen = false;
    saveError = '';
    onClose();
  }

  function go(url: string | null) {
    close();
    if (url) router.get(url, {}, { preserveScroll: true });
  }

  function applyFavorite(fav: FavoriteView) {
    close();
    if (!isFavoriteCurrent(fav, timeline)) navigateToFavorite(fav, timeline.viewConfig);
  }

  // ── Save current ──────────────────────────────────────────────────────────
  let saveFormOpen = $state(false);
  let favName = $state('');
  let isSaving = $state(false);
  let saveError = $state('');

  function startSave() {
    favName = defaultFavoriteName(timeline);
    saveError = '';
    saveFormOpen = true;
  }

  function cancelSave() {
    saveFormOpen = false;
    saveError = '';
  }

  async function save(event: SubmitEvent) {
    event.preventDefault();
    const name = favName.trim();
    if (!name || isSaving) return;
    isSaving = true;
    saveError = '';
    try {
      await addFavorite({
        name,
        view: timeline.view,
        scale: timeline.scale,
        range: timeline.range,
        display: timeline.display,
      });
      saveFormOpen = false;
    } catch (e) {
      saveError = e instanceof Error && e.message ? e.message : 'Could not save. Try again.';
    } finally {
      isSaving = false;
    }
  }

  // Keyboard/mouse users land in the name field; on touch the user taps it,
  // which raises the keyboard natively (plans/mobile-v2.md §3.4).
  function focusUnlessTouch(node: HTMLInputElement) {
    if (!isCoarsePointer()) node.select();
  }
</script>

<MobileSheet {open} onClose={close} direction="bottom" title="View options">
  <div class="vos">
    {#if scaleOptions.length > 1}
      <section class="vos-group">
        <h3 class="vos-heading">Scale</h3>
        <SegmentedControl
          label="Scale"
          value={timeline.scale}
          options={scaleOptions.map((s) => ({ value: s, label: SCALE_LABELS[s] }))}
          onChange={(s) => go(timelineUrlForScale(timeline, s))}
        />
      </section>
    {/if}

    {#if rangeOptions.length > 1}
      <section class="vos-group">
        <h3 class="vos-heading">Range</h3>
        <SegmentedControl
          label="Range"
          value={timeline.range}
          options={rangeOptions.map((r) => ({ value: r, label: RANGE_LABELS[r] ?? r }))}
          onChange={(r) => go(timelineUrlForRange(timeline, r))}
        />
      </section>
    {/if}

    {#if displayOptions.length > 1}
      <section class="vos-group">
        <h3 class="vos-heading">Show</h3>
        <SegmentedControl
          label="Show"
          value={timeline.display}
          options={displayOptions.map((d) => ({ value: d, label: DISPLAY_LABELS[d] }))}
          onChange={(d) => go(timelineUrlForDisplay(timeline, d))}
        />
      </section>
    {/if}

    <section class="vos-group">
      <h3 class="vos-heading">Favorites</h3>
      {#if favorites.length > 0 || canSave}
        <div class="vos-chips">
          {#each favorites as fav (fav.id)}
            {@const current = isFavoriteCurrent(fav, timeline)}
            <button
              type="button"
              class="vos-chip"
              class:on={current}
              aria-current={current ? 'true' : undefined}
              onclick={() => applyFavorite(fav)}
            >
              <span aria-hidden="true">{fav.icon || '⭐'}</span>
              {fav.name}
            </button>
          {/each}
          {#if canSave && !saveFormOpen}
            <button type="button" class="vos-chip vos-chip-add" onclick={startSave}>
              + Save current
            </button>
          {/if}
        </div>
      {:else}
        <p class="vos-empty">Save a Days, Focus or Tasks view to see it here.</p>
      {/if}

      {#if saveFormOpen}
        <form class="vos-save" onsubmit={save}>
          <input
            class="vos-save-input"
            type="text"
            autocomplete="off"
            aria-label="Favorite name"
            maxlength="50"
            enterkeyhint="done"
            bind:value={favName}
            use:focusUnlessTouch
          />
          <button type="submit" class="vos-save-btn" disabled={!favName.trim() || isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" class="vos-cancel-btn" onclick={cancelSave}>Cancel</button>
        </form>
        {#if saveError}
          <p class="vos-error" role="alert">{saveError}</p>
        {/if}
      {/if}
    </section>

    <button type="button" class="vos-done" onclick={close}>Done</button>
  </div>
</MobileSheet>

<style>
  .vos {
    padding: 4px 16px 12px;
  }

  .vos-group {
    padding: 8px 0;
  }

  .vos-heading {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6b7280;
  }

  .vos-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .vos-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 44px;
    max-width: 100%;
    padding: 0 14px;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    background: #fff;
    color: #111827;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .vos-chip.on {
    border-color: #2563eb;
    background: #eff6ff;
    color: #1d4ed8;
  }

  .vos-chip:active {
    background: #f3f4f6;
  }

  .vos-chip-add {
    border-style: dashed;
    color: #2563eb;
  }

  .vos-empty {
    margin: 0;
    font-size: 14px;
    color: #6b7280;
  }

  .vos-save {
    display: flex;
    gap: 8px;
    margin-top: 10px;
  }

  .vos-save-input {
    flex: 1 1 auto;
    min-width: 0;
    height: 44px;
    padding: 0 12px;
    border: 1px solid #d1d5db;
    border-radius: 10px;
    font: inherit;
    /* ≥16px so iOS doesn't zoom on focus */
    font-size: 16px;
  }

  .vos-save-btn,
  .vos-cancel-btn {
    flex-shrink: 0;
    height: 44px;
    padding: 0 14px;
    border-radius: 10px;
    font: inherit;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
  }

  .vos-save-btn {
    border: 0;
    background: #2563eb;
    color: #fff;
  }

  .vos-save-btn:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .vos-cancel-btn {
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #374151;
  }

  .vos-error {
    margin: 6px 0 0;
    font-size: 13px;
    color: #b91c1c;
  }

  .vos-done {
    width: 100%;
    height: 50px;
    margin-top: 12px;
    border: 0;
    border-radius: 14px;
    background: #2563eb;
    color: #fff;
    font: inherit;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
  }
</style>
